import LoginManagementService from '@assets/js/imaios-connection-management/login-management-service';
import {
	getSiteAccess,
	get
} from '@assets/js/imaios-global/imaios-global';
import {
	changeDefinitionFromHash,
	getHashFromSelectedLanguage,
	selectLanguageFromHash
} from '../structures/definition/languageSwitcher/languageSwitcher';
import {
	getHashFromState,
	hasEditState,
	setEditState,
	unsetEditState
} from './components/helper/edit-state-persistance';
import {
	hideDefinitionBlock,
	switchLanguageFromAddDefinition
} from './structure-front-page';

export const editScope = 'description';
// retrieve the last stored hash
const postLoginRestoreKey = 'edit_hash';
const hashFromLocalStorage = localStorage.getItem(postLoginRestoreKey) ?? getHashFromState(editScope);
// clean up the last stored hash. If we need it we have it already, if not it shouldn't be here in the first place.
localStorage.removeItem(postLoginRestoreKey);


/**
 * load and instantiate the form
 * @param {string} languageHash the hash to use for loading and saving the data of the form
 * @return {Promise<void>}
 */
async function startEditor(languageHash) {
	const { createForm } = await import('./main.js');
	setEditState(editScope, languageHash);
	await createForm(languageHash);
	hideDefinitionBlock();
}

/**
 * Check if the user is logged in and if the current language is editable.
 * @return {Promise<boolean>}
 */
async function canOpenEditorManually() {
	await LoginManagementService.awaitConnection();
	return LoginManagementService.isConnected() && getHashFromSelectedLanguage() !== '';
}

/**
 * Check if the user is logged in and if an edit request was performed on the current hash before they logged in.
 * Is only true if the user clicked the edit button when not logged in, got redirected to the login page and successfully logged in.
 * @return {boolean}
 */
function canOpenEditorFromRestoredState() {
	const hashFromSelectedLanguage = getHashFromSelectedLanguage();
	const isEditableHash = hashFromLocalStorage === hashFromSelectedLanguage;

	return LoginManagementService.isConnected() && hashFromLocalStorage !== null && isEditableHash;
}

/**
 * Check if the current user can start an edit session.
 * @param [autoReload=false] if set to `true` and the user isn't logged in, redirect them to the login page.
 * @param [languageHash=''] the hash of the language to edit.
 * @return {Promise<void>}
 */
async function startEditorWithCheck(autoReload = false, languageHash = getHashFromSelectedLanguage()) {
	if (await canOpenEditorManually()) {
		return await startEditor(languageHash);
	}
	if (autoReload) {
		// store the current hash for retrieval once logged in
		localStorage.setItem(postLoginRestoreKey, languageHash);
		window.location.href = `/${getSiteAccess()}/login`;
	}
}

document.getElementById('structure-definitionEdition')
	?.addEventListener('click', () => startEditorWithCheck(true));

document
	.getElementById('structure-add-definition')
	?.addEventListener('click', () =>
		startEditorWithCheck(true, get('currentHash'))
	);


document
	.querySelectorAll('.structure-def-languages')
	?.forEach(element => {
		element.addEventListener('click', async () => {
			const hashLanguageSelected = element.getAttribute('data-attr-hash');
			await changeDefinitionFromHash(hashLanguageSelected);
			selectLanguageFromHash(hashLanguageSelected);
			switchLanguageFromAddDefinition();
		});
	});

async function startEditorOnPageLoaded() {
	await LoginManagementService.awaitConnection();

	if (!LoginManagementService.isConnected()) {
		unsetEditState(editScope);
		return;
	}

	if (
		hasEditState(editScope)
		|| canOpenEditorFromRestoredState()
	) {
		const hash = getHashFromState(editScope);
		await selectLanguageFromHash(hash);
		await changeDefinitionFromHash(hash);
		await startEditor(hash);
	}
}

void startEditorOnPageLoaded();
